import { Fragment, LabelHTMLAttributes, ReactElement } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { IconInfoSmall } from '@components/icons/info-small';
import { Stack } from '@components/stack';
import { getFieldContent } from '@components/text-editors/addons/field-mapping';
import { VisuallyHidden } from '@components/visually-hidden';
import { Color, theme } from '@styles/theme';

export interface FormLabelProps extends Omit<LabelHTMLAttributes<HTMLLabelElement>, 'color'> {
  text?: string;
  color?: Color;
  icon?: ReactElement<IconProps>;
  info?: string;
  helpText?: string;
}

export function FormLabel(props: FormLabelProps) {
  const { text, icon, color, info, helpText, ...rest } = props;

  if (!text) return null;

  const textString = getFieldContent(text);
  const hasAsterisk = textString.endsWith(' *');
  const labelText = hasAsterisk ? text.slice(0, -2) : text;

  return (
    <Label $color={color} {...rest}>
      <Stack space={1} as="span" align="center" orientation="horizontal">
        {icon}
        <span>
          {labelText}
          {hasAsterisk && <Asterisk> *</Asterisk>}
        </span>
        {info && <IconInfoSmall color="purple50" tooltip={info} className="info-icon" />}
      </Stack>
      <span data-c="label-helptext">{helpText}</span>
    </Label>
  );
}

export type FormLabelContainerProps = {
  id?: string;
  label?: string;
  icon?: ReactElement<IconProps>;
  hideLabel?: boolean;
  labelIcon?: ReactElement<IconProps>;
};

export const FormLabelContainer: React.FC<FormLabelContainerProps> = ({
  id,
  hideLabel,
  icon,
  label,
  children,
}) => {
  if (!label) {
    return <>{children}</>;
  }

  const Wrapper = hideLabel ? VisuallyHidden : Fragment;

  return (
    <Stack space={1}>
      <Wrapper>
        <FormLabel htmlFor={id} text={label} icon={icon} />
      </Wrapper>
      {children}
    </Stack>
  );
};

const Asterisk = styled.span({
  '&&': {
    color: theme.color.textBullet,
  },
});

const Label = styled.label<{ $color?: Color }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  font: theme.font.semibold14,
  lineHeight: '1.5rem',
  span: {
    color: p.$color ? theme.color[p.$color] : theme.form.label.color,
  },
  '[data-c="label-helptext"]': {
    color: theme.color.purple60,
    font: theme.font.regular14,
  },
  svg: {
    color: theme.form.label.icon.color,
    ':hover': {
      color: theme.form.label.icon.colorHover,
    },
  },
  '.info-icon': {
    ':hover': {
      color: theme.color.indigo100,
    },
  },

  '@media(max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
  },
}));
